import React, { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { useSiteContext } from "../components/layout/Layout";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Radio, Flame, AlertTriangle, CheckCircle2, XCircle, WifiOff, Lock, RefreshCw, Plus, History, LayoutGrid } from "lucide-react";

const POINT_TYPES = ["smoke", "heat", "mcp", "sounder", "io_module", "input", "output", "other"];
const STATUSES = ["normal", "alarm", "fault", "isolate", "offline"];

const STATUS_CONFIG = {
  normal:  { label: "Normal",  color: "text-emerald-300", dot: "bg-emerald-500",                 icon: CheckCircle2 },
  alarm:   { label: "Alarm",   color: "text-red-300",     dot: "bg-red-500 status-pulse-red",     icon: Flame },
  fault:   { label: "Fault",   color: "text-amber-300",   dot: "bg-amber-500",                    icon: AlertTriangle },
  isolate: { label: "Isolated",color: "text-sky-300",     dot: "bg-sky-500",                      icon: Lock },
  offline: { label: "Offline", color: "text-slate-400",   dot: "bg-slate-500",                    icon: WifiOff },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.offline;
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider ${cfg.color}`}>
      <span className={`w-2 h-2 rounded-full shrink-0 ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

function StatsBar({ stats }) {
  if (!stats) return null;
  return (
    <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
      {[
        { key: "total",   label: "Total",    color: "from-slate-500 to-slate-700" },
        { key: "normal",  label: "Normal",   color: "from-emerald-400 to-emerald-600" },
        { key: "alarm",   label: "Alarm",    color: "from-red-500 to-pink-600" },
        { key: "fault",   label: "Fault",    color: "from-amber-400 to-orange-500" },
        { key: "isolate", label: "Isolated", color: "from-sky-400 to-indigo-500" },
      ].map(({ key, label, color }) => (
        <div key={key} className="glass p-3 rounded-lg text-center">
          <div className={`text-2xl font-extrabold bg-gradient-to-br ${color} bg-clip-text text-transparent`}>
            {stats[key] ?? 0}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-gray-500 mt-0.5">{label}</div>
        </div>
      ))}
    </div>
  );
}

function AddPointDialog({ open, onClose, panels, sites, selectedSite, onSaved }) {
  const [form, setForm] = useState({ panel_id: "", loop_number: "1", address: "", point_type: "smoke", label: "", zone: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.panel_id || !form.address || !form.label) { toast.error("Panel, address and label are required"); return; }
    setBusy(true);
    try {
      await api.post("/addressable/points", {
        ...form,
        loop_number: parseInt(form.loop_number, 10),
        address: parseInt(form.address, 10),
        site_id: panels.find((p) => p.panel_id === form.panel_id)?.site_id || selectedSite,
      });
      toast.success("Point added");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add point");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md">
        <DialogHeader><DialogTitle>Add Addressable Point</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Panel *</label>
            <Select value={form.panel_id} onValueChange={(v) => set("panel_id", v)}>
              <SelectTrigger className="bg-gray-50 border-gray-200"><SelectValue placeholder="Select panel" /></SelectTrigger>
              <SelectContent className="bg-white border-gray-200">
                {panels.map((p) => <SelectItem key={p.panel_id} value={p.panel_id} className="text-gray-900">{p.name || p.panel_id}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Loop #</label>
              <Input type="number" min={1} value={form.loop_number} onChange={(e) => set("loop_number", e.target.value)} className="bg-gray-50 border-gray-200" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Address *</label>
              <Input type="number" min={1} value={form.address} onChange={(e) => set("address", e.target.value)} className="bg-gray-50 border-gray-200" />
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Point Type</label>
            <Select value={form.point_type} onValueChange={(v) => set("point_type", v)}>
              <SelectTrigger className="bg-gray-50 border-gray-200"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-gray-200">
                {POINT_TYPES.map((t) => <SelectItem key={t} value={t} className="text-gray-900 capitalize">{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Label *</label>
            <Input value={form.label} onChange={(e) => set("label", e.target.value)} placeholder="e.g. L1-A05 SMOKE" className="bg-gray-50 border-gray-200" />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Zone</label>
            <Input value={form.zone} onChange={(e) => set("zone", e.target.value)} placeholder="e.g. Zone 2" className="bg-gray-50 border-gray-200" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-white/15">Cancel</Button>
          <Button disabled={busy} onClick={submit} className="f360-gradient border-0">{busy ? "Saving…" : "Add Point"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatusChangeDialog({ point, open, onClose, onChanged }) {
  const [newStatus, setNewStatus] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open && point) { setNewStatus(point.status); setNote(""); } }, [open, point]);

  const submit = async () => {
    if (!newStatus) return;
    setBusy(true);
    try {
      const r = await api.patch(`/addressable/points/${point.point_id}/status`, { status: newStatus, note });
      if (r.data.changed) {
        toast.success(`Status changed: ${r.data.old_status} → ${r.data.new_status}`);
      } else {
        toast.info("Status unchanged");
      }
      onChanged();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update status");
    } finally { setBusy(false); }
  };

  if (!point) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-sm">
        <DialogHeader><DialogTitle>Change Point Status</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div className="text-sm text-gray-600 font-semibold">{point.label}</div>
          <div className="text-xs text-gray-500">Loop {point.loop_number} · Address {point.address}</div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">New Status</label>
            <Select value={newStatus} onValueChange={setNewStatus}>
              <SelectTrigger className="bg-gray-50 border-gray-200"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-gray-200">
                {STATUSES.map((s) => <SelectItem key={s} value={s} className="text-gray-900 capitalize">{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Note (optional)</label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Activation zone 3" className="bg-gray-50 border-gray-200" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-white/15">Cancel</Button>
          <Button disabled={busy} onClick={submit} className="f360-gradient border-0">{busy ? "Saving…" : "Update"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AddressablePanel() {
  const { sites, selectedSite } = useSiteContext();
  const { user } = useAuth();

  const [panels, setPanels] = useState([]);
  const [selectedPanel, setSelectedPanel] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [points, setPoints] = useState([]);
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("points");
  const [addOpen, setAddOpen] = useState(false);
  const [statusDialog, setStatusDialog] = useState({ open: false, point: null });
  const [busy, setBusy] = useState(false);

  const isAdmin = ["admin", "customer_admin", "super_admin"].includes(user?.role);

  const loadPanels = async () => {
    const params = {};
    if (selectedSite && selectedSite !== "all") params.site_id = selectedSite;
    try {
      const r = await api.get("/panels", { params });
      setPanels(r.data || []);
    } catch { setPanels([]); }
  };

  const loadPoints = async () => {
    const params = {};
    if (selectedPanel !== "all") params.panel_id = selectedPanel;
    else if (selectedSite && selectedSite !== "all") params.site_id = selectedSite;
    if (statusFilter !== "all") params.status = statusFilter;
    try {
      const r = await api.get("/addressable/points", { params });
      setPoints(r.data || []);
    } catch { setPoints([]); }
  };

  const loadEvents = async () => {
    const params = { limit: 200 };
    if (selectedPanel !== "all") params.panel_id = selectedPanel;
    else if (selectedSite && selectedSite !== "all") params.site_id = selectedSite;
    try {
      const r = await api.get("/addressable/events", { params });
      setEvents(r.data || []);
    } catch { setEvents([]); }
  };

  const loadStats = async () => {
    if (selectedPanel && selectedPanel !== "all") {
      try {
        const r = await api.get(`/addressable/stats/${selectedPanel}`);
        setStats(r.data);
      } catch { setStats(null); }
    } else {
      setStats(null);
    }
  };

  const reload = () => { loadPoints(); loadEvents(); loadStats(); };

  useEffect(() => { loadPanels(); }, [selectedSite]);
  useEffect(() => { reload(); }, [selectedSite, selectedPanel, statusFilter]);

  // Auto-refresh
  useEffect(() => {
    const i = setInterval(reload, 15000);
    return () => clearInterval(i);
  }, [selectedSite, selectedPanel, statusFilter]);

  const filteredPoints = useMemo(() => {
    if (!searchTerm) return points;
    const q = searchTerm.toLowerCase();
    return points.filter((p) =>
      p.label?.toLowerCase().includes(q) ||
      p.zone?.toLowerCase().includes(q) ||
      String(p.address).includes(q) ||
      p.point_type?.includes(q)
    );
  }, [points, searchTerm]);

  const currentSite = sites.find((s) => s.site_id === selectedSite);

  return (
    <div className="space-y-6" data-testid="addressable-panel-page">
      <div>
        <div className="text-xs text-gray-500">Monitoring → Addressable Panel</div>
        <h1 className="text-3xl md:text-4xl font-extrabold flex items-center gap-2">
          <Radio className="w-7 h-7 text-orange-400" strokeWidth={2.5} /> Addressable Fire Panel
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Points list · status monitoring · event log
          {currentSite ? ` · ${currentSite.name}` : ""}
        </p>
      </div>

      {/* Stats */}
      <StatsBar stats={stats} />

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <Select value={selectedPanel} onValueChange={(v) => setSelectedPanel(v)}>
          <SelectTrigger className="w-48 bg-gray-50 border-gray-200 text-sm">
            <SelectValue placeholder="All Panels" />
          </SelectTrigger>
          <SelectContent className="bg-white border-gray-200">
            <SelectItem value="all" className="text-gray-900">All Panels</SelectItem>
            {panels.map((p) => (
              <SelectItem key={p.panel_id} value={p.panel_id} className="text-gray-900">
                {p.name || p.panel_id}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36 bg-gray-50 border-gray-200 text-sm">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent className="bg-white border-gray-200">
            <SelectItem value="all" className="text-gray-900">All Status</SelectItem>
            {STATUSES.map((s) => (
              <SelectItem key={s} value={s} className="text-gray-900 capitalize">{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Input
          placeholder="Search label, zone, address…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-56 bg-gray-50 border-gray-200 text-sm"
        />

        <Button
          variant="outline"
          className="border-white/15 text-gray-500 hover:text-gray-900 ml-auto"
          onClick={reload}
          data-testid="refresh-btn"
        >
          <RefreshCw className="w-4 h-4" strokeWidth={2.5} />
        </Button>

        {isAdmin && (
          <Button className="f360-gradient border-0" onClick={() => setAddOpen(true)} data-testid="add-point-btn">
            <Plus className="w-4 h-4 mr-1" strokeWidth={2.5} /> Add Point
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-50 p-1 rounded-lg w-fit">
        {[
          { key: "points", label: "Points", icon: LayoutGrid },
          { key: "events", label: "Events Feed", icon: History },
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${tab === key ? "bg-gray-100 text-gray-900" : "text-gray-500 hover:text-gray-900"}`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {/* Points Tab */}
      {tab === "points" && (
        <div className="glass rounded-lg overflow-hidden" data-testid="points-table">
          {filteredPoints.length === 0 ? (
            <div className="py-16 text-center text-gray-500">
              {points.length === 0 ? "No addressable points found for the selected filter." : "No points match your search."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3">Label</th>
                    <th className="text-left px-4 py-3">Loop</th>
                    <th className="text-left px-4 py-3">Address</th>
                    <th className="text-left px-4 py-3">Type</th>
                    <th className="text-left px-4 py-3">Zone</th>
                    <th className="text-left px-4 py-3">Status</th>
                    {isAdmin && <th className="px-4 py-3" />}
                  </tr>
                </thead>
                <tbody>
                  {filteredPoints.map((pt) => (
                    <tr
                      key={pt.point_id}
                      className={`border-b border-white/5 hover:bg-white/3 transition ${pt.status === "alarm" ? "bg-red-500/5" : pt.status === "fault" ? "bg-amber-500/5" : ""}`}
                      data-testid={`point-row-${pt.point_id}`}
                    >
                      <td className="px-4 py-3 font-semibold">{pt.label}</td>
                      <td className="px-4 py-3 text-gray-500">{pt.loop_number}</td>
                      <td className="px-4 py-3 text-gray-500 font-mono">{pt.address}</td>
                      <td className="px-4 py-3">
                        <span className="text-[10px] uppercase tracking-wider bg-gray-100 px-2 py-0.5 rounded">
                          {pt.point_type}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500">{pt.zone || "—"}</td>
                      <td className="px-4 py-3">
                        <StatusBadge status={pt.status} />
                      </td>
                      {isAdmin && (
                        <td className="px-4 py-3 text-right">
                          <Button
                            size="sm"
                            className="bg-gradient-to-r from-sky-500 to-indigo-600 border-0 text-white text-xs h-7 hover:from-sky-600 hover:to-indigo-700 transition"
                            onClick={() => setStatusDialog({ open: true, point: pt })}
                            data-testid={`change-status-${pt.point_id}`}
                          >
                            Change Status
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-4 py-3 text-xs text-gray-500 border-t border-gray-200">
                Showing {filteredPoints.length} of {points.length} points
              </div>
            </div>
          )}
        </div>
      )}

      {/* Events Feed Tab */}
      {tab === "events" && (
        <div className="glass rounded-lg overflow-hidden" data-testid="events-feed">
          {events.length === 0 ? (
            <div className="py-16 text-center text-gray-500">No events recorded.</div>
          ) : (
            <div className="divide-y divide-white/5">
              {events.map((ev) => {
                const oldCfg = STATUS_CONFIG[ev.old_status] || STATUS_CONFIG.offline;
                const newCfg = STATUS_CONFIG[ev.new_status] || STATUS_CONFIG.offline;
                return (
                  <div key={ev.event_id} className="px-5 py-3 flex items-start gap-4 hover:bg-white/3 transition" data-testid={`event-${ev.event_id}`}>
                    <div className={`mt-1 w-2 h-2 rounded-full shrink-0 ${newCfg.dot}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-sm">{ev.label}</span>
                        <span className="text-[10px] text-gray-500">Loop {ev.loop_number} · #{ev.address}</span>
                      </div>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className={`text-xs ${oldCfg.color}`}>{oldCfg.label}</span>
                        <span className="text-slate-600">→</span>
                        <span className={`text-xs font-bold ${newCfg.color}`}>{newCfg.label}</span>
                        {ev.note && <span className="text-xs text-gray-500 ml-2">· {ev.note}</span>}
                      </div>
                      {ev.triggered_by && (
                        <div className="text-[10px] text-gray-500 mt-0.5">by {ev.triggered_by}</div>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 shrink-0">
                      {ev.occurred_at ? new Date(ev.occurred_at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" }) : "—"}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Add Point Dialog */}
      <AddPointDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        panels={panels}
        sites={sites}
        selectedSite={selectedSite}
        onSaved={reload}
      />

      {/* Status Change Dialog */}
      <StatusChangeDialog
        open={statusDialog.open}
        point={statusDialog.point}
        onClose={() => setStatusDialog({ open: false, point: null })}
        onChanged={reload}
      />
    </div>
  );
}
